import {
  formatJourneyDistance,
  journeyDropoffAddress,
  journeyPickupAddress,
  journeyWaypoints,
} from "./normalizeBookingForm";
import { formatUkPickupDateTime } from "./dateUtils";

export type DriverJobBooking = {
  id: string;
  bookingId: number;
  jobNo?: string | null;
  waitTime?: string | null;
  vehicleNo?: string | null;
  vehicleMake?: string | null;
  vehicleModel?: string | null;
  vehicleTypeName?: string | null;
  pickupDateTime?: string | Date | null;
  pickupPostalCode?: string | null;
  dropoffPostalCode?: string | null;
  pasengerName?: string | null;
  surName?: string | null;
  cellno?: string | null;
  noofPassenger?: number | null;
  luggagekg?: number | string | null;
  luggageDetail?: string | null;
  childSeat?: number | null;
  disableSeat?: number | null;
  returnTrip?: number | null;
  meetGreet?: number | null;
  noOfAdult?: number | null;
  noOfChild?: number | null;
  arrivalFrom?: string | null;
  flightNo?: string | null;
  terminal?: string | null;
  doorNo?: string | null;
  additionalInfo?: string | null;
  manualPickupAddress?: string | null;
  manualDropOffAddress?: string | null;
  startpoint?: string | null;
  startpoint1?: string | null;
  startpoint2?: string | null;
  startpoint3?: string | null;
  endposition?: string | null;
  endposition1?: string | null;
  endposition2?: string | null;
  endposition3?: string | null;
  miles?: number | string | null;
  distance?: number | string | null;
  returnDate?: string | Date | null;
  returnTimeStr?: string | null;
  returnStartpoint?: string | null;
  returnEndposition?: string | null;
};

function esc(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function formatPickupDate(value: string | Date | null | undefined): string {
  if (!value) return "—";
  return formatUkPickupDateTime(value);
}

function bookingReference(booking: DriverJobBooking): string {
  if (booking.bookingId > 0) return `#${booking.bookingId}`;
  return booking.id.slice(0, 8).toUpperCase();
}

function passengerName(booking: DriverJobBooking): string {
  return [booking.pasengerName, booking.surName].filter(Boolean).join(" ") || "—";
}

function formatMiles(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  const rounded = Number.isInteger(n) ? String(n) : n.toFixed(1);
  return `${rounded} miles`;
}

function row(label: string, value: string): string {
  return `<tr><th>${label}</th><td>${value}</td></tr>`;
}

function line(label: string, value: unknown): string | null {
  if (value === null || value === undefined || value === "") return null;
  return `${label}: ${value}`;
}

function joinLines(lines: Array<string | null | undefined>): string {
  return lines.filter((entry): entry is string => Boolean(entry)).join("\n");
}

/** Plain-text job sheet for WhatsApp / SMS / email to the driver (no pricing). */
export function formatDriverJobText(
  booking: DriverJobBooking,
  contactPhone = "+44 20 8432 0829",
): string {
  const ref = bookingReference(booking);
  const via = journeyWaypoints(booking);
  const services = [
    booking.meetGreet ? "Meet & greet" : null,
    booking.childSeat ? "Child seat" : null,
    booking.disableSeat ? "Disabled access" : null,
    booking.returnTrip ? "Return trip" : null,
  ].filter(Boolean);

  const vehicleBits = [
    booking.vehicleTypeName,
    booking.vehicleMake,
    booking.vehicleModel,
    booking.vehicleNo,
  ]
    .map((value) => (value ? String(value).trim() : ""))
    .filter(Boolean);

  const sections = [
    `DRIVER JOB ${ref}`,
    "",
    "JOB",
    joinLines([
      booking.jobNo ? line("Job number", booking.jobNo) : null,
      line("Pickup time", formatPickupDate(booking.pickupDateTime)),
      line("Distance", formatJourneyDistance(booking)),
      vehicleBits.length ? line("Vehicle", vehicleBits.join(" · ")) : null,
    ]),
    "",
    "ROUTE",
    joinLines([
      line("Pickup", journeyPickupAddress(booking)),
      line("Pickup postcode", booking.pickupPostalCode),
      via.length ? line("Via", via.join(" → ")) : null,
      line("Drop-off", journeyDropoffAddress(booking)),
      line("Drop-off postcode", booking.dropoffPostalCode),
      booking.waitTime ? line("Wait time", booking.waitTime) : null,
    ]),
    "",
    "PASSENGER",
    joinLines([
      line("Name", passengerName(booking)),
      line("Phone", booking.cellno),
      line("Adults", booking.noOfAdult),
      line("Children", booking.noOfChild),
      line("Total passengers", booking.noofPassenger),
      line("Luggage", booking.luggageDetail || booking.luggagekg),
    ]),
  ];

  if (booking.flightNo || booking.terminal || booking.arrivalFrom || booking.doorNo) {
    sections.push(
      "",
      "FLIGHT / PICKUP NOTES",
      joinLines([
        booking.flightNo ? line("Flight", booking.flightNo) : null,
        booking.terminal ? line("Terminal", booking.terminal) : null,
        booking.arrivalFrom ? line("Arrival from", booking.arrivalFrom) : null,
        booking.doorNo ? line("Door / gate", booking.doorNo) : null,
      ]),
    );
  }

  if (services.length) {
    sections.push("", "SERVICES", line("Extras", services.join(", ")) ?? "");
  }

  if (booking.returnTrip) {
    sections.push(
      "",
      "RETURN JOURNEY",
      joinLines([
        line("Return pickup", booking.returnStartpoint),
        line("Return drop-off", booking.returnEndposition),
        line(
          "Return date/time",
          `${formatPickupDate(booking.returnDate)}${booking.returnTimeStr ? ` ${booking.returnTimeStr}` : ""}`,
        ),
      ]),
    );
  }

  if (booking.additionalInfo?.trim()) {
    sections.push("", "ADDITIONAL INFORMATION", booking.additionalInfo.trim());
  }

  sections.push("", `Office contact: ${contactPhone}`);

  return sections.join("\n");
}

export async function copyDriverJobText(
  booking: DriverJobBooking,
  contactPhone = "+44 20 8432 0829",
): Promise<void> {
  const text = formatDriverJobText(booking, contactPhone);

  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  document.body.removeChild(textarea);

  if (!copied) {
    throw new Error("Could not copy to clipboard.");
  }
}

/** Opens a print-ready job sheet for the driver (no pricing). */
export function exportDriverJobSheet(
  booking: DriverJobBooking,
  contactPhone = "+44 20 8432 0829",
): void {
  const ref = bookingReference(booking);
  const via = journeyWaypoints(booking);
  const services = [
    booking.meetGreet ? "Meet & greet" : null,
    booking.childSeat ? "Child seat" : null,
    booking.disableSeat ? "Disabled access" : null,
    booking.returnTrip ? "Return trip" : null,
  ].filter(Boolean);

  const vehicleBits = [
    booking.vehicleTypeName,
    booking.vehicleMake,
    booking.vehicleModel,
    booking.vehicleNo,
  ]
    .map((v) => (v ? String(v).trim() : ""))
    .filter(Boolean);

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Job sheet ${esc(ref)}</title>
  <style>
    * { box-sizing: border-box; }
    body {
      font-family: Arial, Helvetica, sans-serif;
      color: #111;
      margin: 0;
      padding: 24px;
      line-height: 1.4;
    }
    h1 { font-size: 22px; margin: 0 0 4px; }
    .sub { color: #555; margin: 0 0 20px; font-size: 13px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 18px; }
    th, td { text-align: left; padding: 8px 10px; border-bottom: 1px solid #ddd; vertical-align: top; }
    th { width: 160px; color: #444; font-weight: 600; font-size: 12px; text-transform: uppercase; letter-spacing: 0.03em; }
    td { font-size: 14px; font-weight: 500; }
    .section { font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; margin: 18px 0 6px; color: #0d47a1; }
    .notes { white-space: pre-wrap; }
    .footer { margin-top: 28px; font-size: 12px; color: #555; border-top: 1px solid #ddd; padding-top: 12px; }
    @media print {
      body { padding: 0; }
      .no-print { display: none !important; }
    }
  </style>
</head>
<body>
  <button class="no-print" onclick="window.print()" style="margin-bottom:16px;padding:8px 14px;font-size:14px;cursor:pointer;">
    Print / Save as PDF
  </button>
  <h1>Driver job sheet ${esc(ref)}</h1>
  <p class="sub">Give this sheet to the driver · Pricing omitted</p>

  <div class="section">Job</div>
  <table>
    ${row("Reference", esc(ref))}
    ${booking.jobNo ? row("Job number", esc(booking.jobNo)) : ""}
    ${row("Pickup time", esc(formatPickupDate(booking.pickupDateTime)))}
    ${row("Distance", esc(formatJourneyDistance(booking)))}
    ${vehicleBits.length ? row("Vehicle", esc(vehicleBits.join(" · "))) : ""}
  </table>

  <div class="section">Route</div>
  <table>
    ${row("Pickup", esc(journeyPickupAddress(booking)))}
    ${row("Pickup postcode", esc(booking.pickupPostalCode))}
    ${via.length ? row("Via", esc(via.join(" → "))) : ""}
    ${row("Drop-off", esc(journeyDropoffAddress(booking)))}
    ${row("Drop-off postcode", esc(booking.dropoffPostalCode))}
    ${booking.waitTime ? row("Wait time", esc(booking.waitTime)) : ""}
  </table>

  <div class="section">Passenger</div>
  <table>
    ${row("Name", esc(passengerName(booking)))}
    ${row("Phone", esc(booking.cellno))}
    ${row("Adults", esc(booking.noOfAdult))}
    ${row("Children", esc(booking.noOfChild))}
    ${row("Total passengers", esc(booking.noofPassenger))}
    ${row("Luggage", esc(booking.luggageDetail || booking.luggagekg))}
  </table>

  ${
    booking.flightNo || booking.terminal || booking.arrivalFrom || booking.doorNo
      ? `<div class="section">Flight / pickup notes</div>
  <table>
    ${booking.flightNo ? row("Flight", esc(booking.flightNo)) : ""}
    ${booking.terminal ? row("Terminal", esc(booking.terminal)) : ""}
    ${booking.arrivalFrom ? row("Arrival from", esc(booking.arrivalFrom)) : ""}
    ${booking.doorNo ? row("Door / gate", esc(booking.doorNo)) : ""}
  </table>`
      : ""
  }

  ${
    services.length
      ? `<div class="section">Services</div>
  <table>${row("Extras", esc(services.join(", ")))}</table>`
      : ""
  }

  ${
    booking.returnTrip
      ? `<div class="section">Return journey</div>
  <table>
    ${row("Return pickup", esc(booking.returnStartpoint))}
    ${row("Return drop-off", esc(booking.returnEndposition))}
    ${row("Return date/time", esc(formatPickupDate(booking.returnDate) + (booking.returnTimeStr ? ` ${booking.returnTimeStr}` : "")))}
  </table>`
      : ""
  }

  ${
    booking.additionalInfo
      ? `<div class="section">Additional information</div>
  <p class="notes">${esc(booking.additionalInfo)}</p>`
      : ""
  }

  <div class="footer">
    Office contact: ${esc(contactPhone)}
  </div>
  <script>
    window.addEventListener('load', function () {
      setTimeout(function () { window.print(); }, 250);
    });
  </script>
</body>
</html>`;

  const win = window.open("", "_blank", "noopener,noreferrer,width=800,height=900");
  if (!win) {
    throw new Error("Pop-up blocked. Allow pop-ups to export the job sheet.");
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
}
